import React from 'react';
import styled from 'styled-components';

import { useSidebarCtx } from './SidebarContext';

// ============================================================
//     TYPES
// ============================================================
interface MenuProps extends React.ComponentPropsWithoutRef<'ul'> {
  /**
   * Whether to render the menu as a nested submenu
   *
   * - Adds additional margin and padding to the left of the menu to indent the submenu items
   *
   * @default false
   */
  isSubmenu?: boolean;
}

// ============================================================
//     STYLES
// ============================================================
const StyledMenu = styled.ul({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--cl-space-1)',

  // Guard against Daffy's legacy global list styles
  margin: 0,
  padding: 0,
  listStyle: 'none',

  '&[data-submenu="true"]': {
    marginLeft: 'var(--cl-space-5)', // Indents the left border
    paddingLeft: 'var(--cl-space-3)', // Distance from left border
    paddingBlock: 'var(--cl-space-1)',
    paddingRight: 2, // Required to keep the subMenu button's focus ring from getting cutoff by any overflow: hidden
    borderLeft: '1px solid var(--cl-border-default)',

    // Hide submenu items when the sidebar is collapsed
    '&[data-expanded="false"]': {
      display: 'none',
    },
  },
});

// ============================================================
//     COMPONENT
// ============================================================
/**
 * A wrapper for `Sidebar.MenuItem` components
 *
 * - Renders a styled `ul` element
 * - Applies submenu styling when `submenu` is `true`
 * - Handles dynamic styling based on the sidebar expansion state
 *
 * @example
 * ```tsx
 * <Sidebar.Menu isSubmenu>
 *   <Sidebar.MenuItem>
 *     <Sidebar.MenuButton>Home</Sidebar.MenuButton>
 *   </Sidebar.MenuItem>
 * </Sidebar.Menu>
 * ```
 */
export const Menu = React.forwardRef<HTMLUListElement, MenuProps>((props, forwardedRef) => {
  const { isSubmenu, ...restProps } = props;
  const { isExpanded } = useSidebarCtx();
  return (
    <StyledMenu
      {...restProps}
      ref={forwardedRef}
      data-component="Sidebar.Menu"
      data-expanded={isExpanded}
      data-submenu={isSubmenu}
    />
  );
});

Menu.displayName = 'Sidebar.Menu' as const;
